import React from 'react';
import { Flex, Heading, Text, Image, Button } from 'rebass';
import { withRouter } from 'react-router';
import colors from 'theme/colors';
import styled from 'styled-components';
import Candidate from 'types/candidates';
import { UserRoleType } from 'types/global';
import candidateInfoIcon from 'assets/icons/candidateInfo.png';
import recruiterNotesIcon from 'assets/icons/recruiterNotes.png';
import rejectCandidateIcon from 'assets/icons/rejectCandidate.png';
import expandMenuIcon from 'assets/icons/expandMenu.png';
import SkillBox from 'components/SkillBox';
import Link from 'components/Link';
import DropDown, { DropDownItem } from 'components/DropDown';
import { RECRUITER, EMPLOYER } from 'constants/index';

// Hooks
import useModal from 'hooks/useModal';
import useDropDownHandlers from 'hooks/useDropDownHandlers';
import Icon from 'components/Icon';
import useJobBoardCandidate from '../hooks/useJobBoardCandidate';
import useJobBoardNavigation from '../hooks/useJobBoardNavigation';

const CandidateWrapper = styled(Flex)`
  border: 2px solid ${colors.gray};
`;

const HeadingButton = styled(Button)`
  padding: 0;
  cursor: pointer;
  outline: none;
  user-select: none;
`;

interface JobBoardCandidateProps extends Candidate {
  userRole: UserRoleType;
  match: {
    params: {
      id: string;
    };
  };
}

const JobBoardCandidate: React.FC<any & JobBoardCandidateProps> = (props): JSX.Element => {
  const { userRole, match, ...candidate } = props;
  const { openModalHandler } = useModal();
  const { viewCandidateDetailsHandler } = useJobBoardNavigation();
  const { isDropDownVisible, openDropDown, closeDropDown } = useDropDownHandlers();
  const {
    moveUserCandidatesHandler,
    rejectUserCandidateHandler,
    removeRecruiterCandidateHandler,
    stages,
    currentStage,
    candidateNote,
  } = useJobBoardCandidate(+match.params.id, candidate);

  return (
    <>
      {userRole === RECRUITER && (
        <CandidateWrapper width="100%" flexDirection="column" alignItems="center" p={2} mb={2}>
          <Flex
            width="100%"
            alignItems="flex-start"
            justifyContent="space-between"
            mb={2}
            css={`
              border-bottom: 1px solid ${colors.gray};
            `}
          >
            <Heading fontSize="20px" pb={2}>
              {`${candidate.firstName} ${candidate.lastName}`}
            </Heading>
            <Link to={`/candidate-details/${candidate.id}`}>
              <Image src={candidateInfoIcon} />
            </Link>
          </Flex>
          <Flex
            width="100%"
            mb={2}
            pb={2}
            css={`
              border-bottom: 1px solid ${colors.gray};
            `}
          >
            {candidate.skills.map((skill: string) => (
              <SkillBox key={skill} mr={1} py="2px">
                {skill}
              </SkillBox>
            ))}
          </Flex>
          <HeadingButton
            width="100%"
            fontSize="24px"
            fontWeight={6}
            bg="transparent"
            mb={3}
            onClick={openModalHandler({
              title: 'editNotes',
              data: {
                candidateId: candidate.id,
                noteId: candidateNote && candidateNote.id,
                note: candidateNote && candidateNote.message,
              },
            })}
          >
            <Flex width="100%" alignItems="center">
              <Image src={recruiterNotesIcon} mr={2} />
              <Text fontSize="14px" fontWeight={6} color="primary">
                View/edit notes
              </Text>
            </Flex>
          </HeadingButton>
          <HeadingButton
            width="100%"
            fontSize="24px"
            fontWeight={6}
            bg="transparent"
            mb={3}
            onClick={openModalHandler({
              title: 'sendMessages',
              data: {
                candidateId: candidate.id,
              },
            })}
          >
            <Flex width="100%" alignItems="center">
              <Icon type="contact" />
              <Text fontSize="14px" fontWeight={6} ml={1} color="primary">
                Contact Company
              </Text>
            </Flex>
          </HeadingButton>
          {currentStage.title === 'Placement' && (
            <>
              <HeadingButton
                width="100%"
                fontSize="24px"
                fontWeight={6}
                bg="transparent"
                mb={3}
                onClick={openModalHandler({
                  title: 'attachFile',
                  data: {
                    label: 'Please, attach accepted Job offer',
                    attachedFileType: 'signed-offer',
                    attachedFilePropName: 'signedOfferUrl',
                    stageTitle: currentStage.title,
                    candidateId: candidate.id,
                  },
                })}
              >
                <Flex width="100%" alignItems="center">
                  <Icon type="attachFile" color="black" />
                  <Text fontSize="14px" fontWeight={6} color="primary">
                    Add accepted job Offer
                  </Text>
                </Flex>
              </HeadingButton>
              <HeadingButton
                width="100%"
                fontSize="24px"
                fontWeight={6}
                bg="transparent"
                mb={3}
                onClick={openModalHandler({
                  title: 'attachFile',
                  data: {
                    label: 'Please, attach your Invoice',
                    attachedFileType: 'invoice',
                    attachedFilePropName: 'invoiceUrl',
                    stageTitle: currentStage.title,
                    candidateId: candidate.id,
                  },
                })}
              >
                <Flex width="100%" alignItems="center">
                  <Icon type="attachFile" color="black" />
                  <Text fontSize="14px" fontWeight={6} color="primary">
                    Add invoice
                  </Text>
                </Flex>
              </HeadingButton>
            </>
          )}
          <HeadingButton
            width="100%"
            fontSize="24px"
            fontWeight={6}
            bg="transparent"
            onClick={openModalHandler({
              title: 'confirm',
              data: {
                title: 'Are you sure that you want to remove candidate?',
                confirmHandler: removeRecruiterCandidateHandler,
              },
            })}
          >
            <Flex width="100%" alignItems="center">
              <Image src={rejectCandidateIcon} mr={2} />
              <Text fontSize="14px" fontWeight={6} color="primary">
                Remove from board
              </Text>
            </Flex>
          </HeadingButton>
        </CandidateWrapper>
      )}
      {userRole === EMPLOYER && (
        <CandidateWrapper width="100%" flexDirection="column" alignItems="center" p={2} mb={2}>
          <Flex width="100%" alignItems="flex-start" justifyContent="space-between">
            <Heading fontSize="20px" fontWeight={6} css="text-decoration: underline">
              {`${candidate.firstName} ${candidate.lastName}`}
            </Heading>
            <HeadingButton bg="transparent">
              <Flex css="position: relative">
                <Image src={expandMenuIcon} onClick={() => openDropDown()} />
                <DropDown
                  visible={!!isDropDownVisible}
                  onClose={closeDropDown}
                  isLeftSide={candidate.stageId === stages[stages.length - 1].id}
                >
                  <DropDownItem
                    name="View details"
                    icon="info"
                    onClick={viewCandidateDetailsHandler(candidate.id)}
                  />
                  <DropDownItem
                    name="Contact Recruiter"
                    icon="contact"
                    onClick={openModalHandler({
                      title: 'sendMessages',
                      data: {
                        candidateId: candidate.id,
                      },
                    })}
                  />
                  <DropDownItem name="Move to" icon="move">
                    {stages.map((stage: any) => (
                      <DropDownItem
                        key={stage.id}
                        name={stage.title}
                        disabled={candidate.stageId === stage.id}
                        onClick={moveUserCandidatesHandler(stage.id, stage.title)}
                      />
                    ))}
                  </DropDownItem>
                  <DropDownItem
                    name="Reject"
                    icon="reject"
                    onClick={openModalHandler({
                      title: 'rejectReason',
                      data: { onReject: rejectUserCandidateHandler },
                    })}
                  />
                </DropDown>
              </Flex>
            </HeadingButton>
          </Flex>
        </CandidateWrapper>
      )}
    </>
  );
};

export default withRouter(JobBoardCandidate);
